import { createContext, useContext, useMemo, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type CellContext,
  type ColumnDef,
} from '@tanstack/react-table';
import {
  Archive,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Eye,
  FileText,
  HandCoins,
  Link2,
  Loader2,
  Mail,
  MessageSquare,
  MinusCircle,
  MoreHorizontal,
  Plus,
  ShieldCheck,
  TriangleAlert,
  Upload,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { TagBadge } from '@/components/shared/TagBadge';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { SelectionSummary } from '@/components/shared/SelectionSummary';
import { AutoAssignedBadge } from '@/components/shared/AutoAssignedBadge';
import { CorrectionBadge } from '@/components/shared/CorrectionBadge';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';
import type { InvoiceListItem, InvoiceSource, InvoiceStatus, Tag as TagType } from '@ip/shared-types';

interface InvoiceTableProps {
  invoices: InvoiceListItem[];
  companyNip: string;
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  tags: TagType[];
  pdfLoading: Record<string, boolean>;
  onPdfOpen: (invoice: InvoiceListItem) => void;
  onInvoiceSelect?: (invoiceId: string) => void;
  onTagChange: (invoiceId: string, currentTags: string[], tagId: string, checked: boolean) => void;
  onStatusChange: (invoiceId: string, status: InvoiceStatus) => void;
  onDelete: (invoiceId: string, invoiceNumber: string | null) => void;
  /** Single-row "Przypisz do projektu" action — hidden when the user has no
      access to the project list (canAssignProject false). */
  canAssignProject: boolean;
  onAssignProject: (invoice: InvoiceListItem) => void;
  isAdmin: boolean;
  onSortToggle: (columnKey: 'issueDate' | 'createdAt') => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Selected rows with full data (cross-page cache) for the footer sum. */
  selectedItems: InvoiceListItem[];
}

const SOURCE_CONFIG: Record<
  InvoiceSource,
  { Icon: typeof Mail; className: string; labelKey: string }
> = {
  email: { Icon: Mail, className: 'text-sky-600 dark:text-sky-400', labelKey: 'invoices.source.email' },
  upload: { Icon: Upload, className: 'text-emerald-600 dark:text-emerald-400', labelKey: 'invoices.source.upload' },
  ksef: { Icon: ShieldCheck, className: 'text-violet-600 dark:text-violet-400', labelKey: 'invoices.source.ksef' },
  import: { Icon: Archive, className: 'text-slate-500 dark:text-slate-400', labelKey: 'invoices.source.import' },
};

const NOT_APPLICABLE_ISSUES = ['already_paid', 'already_exported', 'non_transfer'];

function formatDate(date: string | null): string {
  return date ? new Date(date).toLocaleDateString('pl-PL') : '-';
}

function SortHeader({
  label,
  columnKey,
  sortBy,
  sortOrder,
  onSortToggle,
}: {
  label: string;
  columnKey: 'issueDate' | 'createdAt';
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSortToggle: (key: 'issueDate' | 'createdAt') => void;
}) {
  const active = sortBy === columnKey;
  return (
    <button
      type="button"
      className="hover:text-foreground -ml-1 flex items-center gap-1 rounded px-1 py-0.5"
      onClick={() => onSortToggle(columnKey)}
    >
      {label}
      {active ? (
        sortOrder === 'asc' ? (
          <ArrowUp className="size-3.5" />
        ) : (
          <ArrowDown className="size-3.5" />
        )
      ) : (
        <ArrowUpDown className="size-3.5 opacity-40" />
      )}
    </button>
  );
}

/**
 * Everything the row tag picker needs, delivered by context.
 *
 * Why context and not props/`columns` deps: TanStack renders every cell as
 * `flexRender(columnDef.cell, ctx)`, i.e. it uses `columnDef.cell` as the React
 * *component type*. Whenever the memoized `columns` array is rebuilt (any of its
 * deps changing — including parent callbacks that are not referentially stable)
 * React sees a brand-new component type at that position and unmounts/remounts
 * the whole cell subtree, popover included.
 *
 * Selecting a row therefore has to re-render InvoiceTable WITHOUT changing the
 * tags cell's component type — otherwise the popover we just opened is torn down
 * on the very next render. So `renderTagsCell` below is a module-level constant
 * (stable identity forever) and the per-render data reaches TagsCell through
 * this context instead.
 */
interface TagPickerContextValue {
  tags: TagType[];
  onTagChange: InvoiceTableProps['onTagChange'];
  /** Only ONE row's popover may be open — opening another closes the previous. */
  openRowId: string | null;
  setOpenRowId: (id: string | null) => void;
}

const TagPickerContext = createContext<TagPickerContextValue>({
  tags: [],
  onTagChange: () => undefined,
  openRowId: null,
  setOpenRowId: () => undefined,
});

/** Row tag picker — same chip-that-opens-a-checkbox-list design as before. */
function TagsCell({ invoice }: { invoice: InvoiceListItem }) {
  const { tags, onTagChange, openRowId, setOpenRowId } = useContext(TagPickerContext);
  const getTagById = (id: string) => tags.find((tag) => tag.id === id);

  return (
    <Popover
      open={openRowId === invoice.id}
      onOpenChange={(open) => setOpenRowId(open ? invoice.id : null)}
    >
      <PopoverTrigger asChild>
        <div className="flex min-h-[22px] cursor-pointer flex-wrap items-center gap-1">
          {invoice.tags.map((id) => {
            const tag = getTagById(id);
            return tag ? <TagBadge key={id} name={tag.name} color={tag.color} /> : null;
          })}
          {invoice.tags.length === 0 && (
            <span className="text-muted-foreground inline-flex items-center gap-0.5 rounded-md border border-dashed px-2 py-0.5 text-xs">
              <Plus className="size-3" /> Add
            </span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-56 space-y-2" align="start">
        {tags.map((tag) => (
          <Label key={tag.id} className="flex items-center gap-2 py-0.5 font-normal">
            <Checkbox
              checked={invoice.tags.includes(tag.id)}
              onCheckedChange={(checked) =>
                onTagChange(invoice.id, invoice.tags, tag.id, checked === true)
              }
            />
            <TagBadge name={tag.name} color={tag.color} />
          </Label>
        ))}
        {tags.length === 0 && (
          <p className="text-muted-foreground text-sm">No tags available</p>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Stable cell renderer — see TagPickerContext. Must stay module-level so its
 * identity never changes across renders of InvoiceTable.
 */
const renderTagsCell = ({ row }: CellContext<InvoiceListItem, unknown>) => (
  <TagsCell invoice={row.original} />
);

export function InvoiceTable(props: InvoiceTableProps) {
  const {
    invoices, companyNip, loading, total, page, pageSize, sortBy, sortOrder,
    selectedIds, onSelectionChange, tags, pdfLoading, onPdfOpen,
    onInvoiceSelect, onTagChange, onStatusChange, onDelete, canAssignProject, onAssignProject, isAdmin,
    onSortToggle, onPageChange, onPageSizeChange, selectedItems,
  } = props;

  // Only ONE row's tag popover may be open at a time — opening another row's
  // closes the previous one. Kept here (not as per-row local state) so the
  // "one open" rule is enforced across rows.
  const [openTagRowId, setOpenTagRowId] = useState<string | null>(null);

  const columns = useMemo<ColumnDef<InvoiceListItem>[]>(
    () => [
      {
        id: 'select',
        size: 40,
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(checked) => table.toggleAllPageRowsSelected(checked === true)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(checked) => row.toggleSelected(checked === true)}
            aria-label="Select row"
          />
        ),
      },
      {
        id: 'pdf',
        size: 48,
        header: () => t('invoices.columns.pdf'),
        cell: ({ row }) =>
          pdfLoading[row.original.id] ? (
            <Loader2 className="text-primary size-4 animate-spin" />
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 text-red-600 dark:text-red-400"
                  aria-label={t('invoices.actions.viewPdf')}
                  onClick={() => onPdfOpen(row.original)}
                >
                  <FileText className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('invoices.actions.viewPdf')}</TooltipContent>
            </Tooltip>
          ),
      },
      {
        id: 'source',
        size: 52,
        header: () => t('invoices.columns.source'),
        cell: ({ row }) => {
          const config = SOURCE_CONFIG[row.original.source] || SOURCE_CONFIG.email;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <config.Icon className={`size-4 ${config.className}`} />
              </TooltipTrigger>
              <TooltipContent>{t(config.labelKey)}</TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        id: 'contractor',
        header: () => t('invoices.columns.contractor'),
        cell: ({ row }) => (
          <div
            className="cursor-pointer"
            onClick={() => onInvoiceSelect?.(row.original.id)}
            title={row.original.contractorName || row.original.contractorNip || '-'}
          >
            <p className="text-primary max-w-56 truncate text-sm font-medium">
              {row.original.contractorName || '-'}
            </p>
            {row.original.contractorNip && (
              <p className="text-muted-foreground font-mono text-xs">
                NIP: {row.original.contractorNip}
              </p>
            )}
          </div>
        ),
      },
      ...(companyNip === 'other'
        ? [
            {
              id: 'detectedNip',
              size: 120,
              header: () => t('invoices.columns.detectedNip'),
              cell: ({ row }: { row: { original: InvoiceListItem } }) => (
                <span className="text-muted-foreground font-mono text-xs">
                  {row.original.customerNip || '—'}
                </span>
              ),
            } satisfies ColumnDef<InvoiceListItem>,
          ]
        : []),
      {
        id: 'invoiceNumber',
        size: 160,
        header: () => t('invoices.columns.invoiceNumber'),
        cell: ({ row }) => (
          <span className="flex items-center gap-1.5">
            <span
              className="text-primary cursor-pointer font-mono text-sm"
              onClick={() => onInvoiceSelect?.(row.original.id)}
            >
              {row.original.invoiceNumber || '-'}
            </span>
            <CorrectionBadge rodzajFaktury={row.original.rodzajFaktury} />
          </span>
        ),
      },
      {
        // Task A3: accounting-notes indicator — icon + count when the
        // invoice has at least one note, empty otherwise.
        id: 'notes',
        size: 56,
        header: () => (
          <Tooltip>
            <TooltipTrigger asChild>
              <MessageSquare className="size-3.5" />
            </TooltipTrigger>
            <TooltipContent>{t('invoices.notes.title')}</TooltipContent>
          </Tooltip>
        ),
        cell: ({ row }) => {
          const count = row.original.notesCount ?? 0;
          if (count === 0) return null;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center gap-1 text-sky-600 dark:text-sky-400">
                  <MessageSquare className="size-4" />
                  <span className="text-xs tabular-nums">{count}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent>{t('invoices.notes.count', { count })}</TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        id: 'issueDate',
        size: 110,
        header: () => (
          <SortHeader
            label={t('invoices.columns.issueDate')}
            columnKey="issueDate"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortToggle={onSortToggle}
          />
        ),
        cell: ({ row }) => <span className="tabular-nums">{formatDate(row.original.issueDate)}</span>,
      },
      {
        id: 'createdAt',
        size: 110,
        header: () => (
          <SortHeader
            label={t('invoices.columns.receivedAt')}
            columnKey="createdAt"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortToggle={onSortToggle}
          />
        ),
        cell: ({ row }) => <span className="tabular-nums">{formatDate(row.original.createdAt)}</span>,
      },
      {
        id: 'status',
        size: 100,
        header: () => t('invoices.columns.status'),
        cell: ({ row }) => (
          <span className="flex items-center gap-1.5">
            <StatusBadge status={row.original.status} />
            {row.original.combinedPaymentId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link2 className="size-3.5 text-sky-600 dark:text-sky-400" />
                </TooltipTrigger>
                <TooltipContent>{t('invoices.combinedPayment.badgeTooltip')}</TooltipContent>
              </Tooltip>
            )}
            {row.original.reimbursementStatus && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <HandCoins className="size-3.5 text-indigo-600 dark:text-indigo-400" />
                </TooltipTrigger>
                <TooltipContent>
                  {t(`invoices.reimbursement.${row.original.reimbursementStatus}`)}
                </TooltipContent>
              </Tooltip>
            )}
          </span>
        ),
      },
      {
        id: 'exportedAt',
        size: 100,
        header: () => t('invoices.columns.exportedAt'),
        cell: ({ row }) => (
          <span className="tabular-nums">{formatDate(row.original.exportedAt)}</span>
        ),
      },
      {
        id: 'tags',
        size: 200,
        header: () => t('invoices.columns.tags'),
        cell: renderTagsCell,
      },
      {
        // Task B3: whole-invoice project name; a "częściowo" badge marks
        // invoices whose lines carry their own assignments (those lines count
        // to their projects, the residual to the invoice's project).
        id: 'project',
        size: 120,
        header: () => t('invoices.columns.project'),
        cell: ({ row }) => {
          const partialBadge = row.original.hasLineProjects ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-muted-foreground text-xs italic">
                  {t('projects.partialBadge')}
                </span>
              </TooltipTrigger>
              <TooltipContent>{t('projects.partialTooltip')}</TooltipContent>
            </Tooltip>
          ) : null;
          if (!row.original.projectName) {
            return partialBadge ?? <span className="text-muted-foreground text-xs">—</span>;
          }
          return (
            <span className="flex items-center gap-1">
              <span
                className="block max-w-28 truncate text-xs"
                title={row.original.projectName}
              >
                {row.original.projectName}
              </span>
              {/* Provenance: rule-made and correction-inherited assignments get an
                  "auto" hint; manual ones keep today's look. */}
              {(row.original.projectAssignedBy === 'rule' ||
                row.original.projectAssignedBy === 'correction') && (
                <AutoAssignedBadge variant={row.original.projectAssignedBy} />
              )}
              {partialBadge}
            </span>
          );
        },
      },
      {
        id: 'exportStatus',
        size: 60,
        header: () => t('invoices.columns.exportStatus'),
        cell: ({ row }) => {
          const issues = row.original.exportIssues;
          if (!issues || issues.length === 0) {
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <CheckCircle2 className="size-4 text-emerald-600 dark:text-emerald-400" />
                </TooltipTrigger>
                <TooltipContent>{t('invoices.export.ready')}</TooltipContent>
              </Tooltip>
            );
          }
          const label = issues.map((i: string) => t(`invoices.export.issues.${i}`)).join(', ');
          // The White List failure is a tax-liability warning, not a mere
          // export blocker — render it loud (red) and first.
          if (issues.includes('not_vat_payer')) {
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <TriangleAlert className="size-4 text-red-600 dark:text-red-500" />
                </TooltipTrigger>
                <TooltipContent>{t('registry.whitelistWarning')} {label}</TooltipContent>
              </Tooltip>
            );
          }
          if (issues.some((i: string) => NOT_APPLICABLE_ISSUES.includes(i))) {
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <MinusCircle className="text-muted-foreground size-4" />
                </TooltipTrigger>
                <TooltipContent>{label}</TooltipContent>
              </Tooltip>
            );
          }
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <TriangleAlert className="size-4 text-amber-600 dark:text-amber-500" />
              </TooltipTrigger>
              <TooltipContent>{label}</TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        id: 'amount',
        size: 140,
        header: () => <div className="text-right">{t('invoices.columns.amount')}</div>,
        cell: ({ row }) => {
          const { amount, currency, hasSplitPayment } = row.original;
          if (amount === null) return <div className="text-right">-</div>;
          return (
            <div className="flex items-center justify-end gap-1.5">
              {hasSplitPayment && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="bg-primary inline-block size-2 shrink-0 animate-pulse rounded-full" />
                  </TooltipTrigger>
                  <TooltipContent>MPP</TooltipContent>
                </Tooltip>
              )}
              <span className="font-mono text-sm font-semibold tabular-nums">
                {formatPLN(amount)} {currency || 'PLN'}
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        size: 72,
        header: () => t('invoices.columns.actions'),
        cell: ({ row }) => (
          <div className="flex items-center gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={t('invoices.actions.open')}
                  onClick={(e) => {
                    e.stopPropagation();
                    onInvoiceSelect?.(row.original.id);
                  }}
                >
                  <Eye className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('invoices.actions.open')}</TooltipContent>
            </Tooltip>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={t('invoices.actions.moreActions')}
                >
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* Opens this row's inline tag popover (anchored to the tags cell). */}
                <DropdownMenuItem onClick={() => setOpenTagRowId(row.original.id)}>
                  {t('invoices.actions.editTags')}
                </DropdownMenuItem>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    {t('invoices.actions.changeStatus')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {(['paid', 'unpaid', 'exported'] as InvoiceStatus[]).map((status) => (
                      <DropdownMenuItem
                        key={status}
                        onClick={() => onStatusChange(row.original.id, status)}
                      >
                        {t(`invoices.status.${status}`)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                {/* Whole-invoice project stays assignable even when lines
                    carry their own projects — it receives the residual. */}
                {canAssignProject && (
                  <DropdownMenuItem onClick={() => onAssignProject(row.original)}>
                    {t('invoices.bulk.assignToProject')}
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() => onDelete(row.original.id, row.original.invoiceNumber)}
                  >
                    {t('invoices.actions.delete')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    // Handlery MUSZĄ być w zależnościach — companyNip może się zmienić bez remountu
    // (przełączenie firmy w sidebarze) i stale closure otwierałby PDF/usuwał w złej firmie.
    // NOTE: the tag picker's inputs (tags, onTagChange, openTagRowId) are
    // deliberately NOT dependencies — they reach the cell via TagPickerContext
    // precisely so that rebuilding `columns` cannot remount the open popover.
    [pdfLoading, sortBy, sortOrder, isAdmin, companyNip, canAssignProject, onPdfOpen, onInvoiceSelect, onDelete, onStatusChange, onAssignProject, onSortToggle],
  );

  const rowSelection = useMemo(
    () => Object.fromEntries(selectedIds.map((id) => [id, true])),
    [selectedIds],
  );

  const table = useReactTable({
    data: invoices,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    manualPagination: true,
    manualSorting: true,
    state: { rowSelection },
    enableRowSelection: true,
    onRowSelectionChange: (updater) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      onSelectionChange(Object.keys(next).filter((k) => next[k]));
    },
  });

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const tagPicker = useMemo<TagPickerContextValue>(
    () => ({
      tags,
      onTagChange,
      openRowId: openTagRowId,
      setOpenRowId: setOpenTagRowId,
    }),
    [tags, onTagChange, openTagRowId],
  );

  return (
    <TagPickerContext.Provider value={tagPicker}>
    <div className="bg-card rounded-xl border">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    style={{ width: header.column.columnDef.size }}
                    className="text-muted-foreground whitespace-nowrap"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={columns.length}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-40 text-center">
                  <p className="font-medium">{t('invoices.noInvoices')}</p>
                  <p className="text-muted-foreground text-sm">
                    {t('invoices.noInvoicesDescription')}
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-2">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2">
        <p className="text-muted-foreground text-sm">
          {t('pagination.showing', { from, to, total })}
        </p>
        <div className="flex items-center gap-3">
          <SelectionSummary items={selectedItems} />
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger size="sm" className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 20, 50, 100].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="size-8" aria-label={t('pagination.firstPage')} disabled={page <= 1} onClick={() => onPageChange(1)}>
              <ChevronsLeft className="size-4" />
            </Button>
            <Button variant="outline" size="icon" className="size-8" aria-label={t('pagination.prevPage')} disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
              <ChevronLeft className="size-4" />
            </Button>
            <span className="px-2 text-sm tabular-nums">
              {page} / {totalPages}
            </span>
            <Button variant="outline" size="icon" className="size-8" aria-label={t('pagination.nextPage')} disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
              <ChevronRight className="size-4" />
            </Button>
            <Button variant="outline" size="icon" className="size-8" aria-label={t('pagination.lastPage')} disabled={page >= totalPages} onClick={() => onPageChange(totalPages)}>
              <ChevronsRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
    </TagPickerContext.Provider>
  );
}
